/* eslint-disable new-cap */
/**
 * Local (in-browser) PDF builder for the order touch screen.
 *
 * When the "build PDF internally" setting is ON, delivery notes / orders / the daily
 * summary are rendered locally with jsPDF and sent straight to the printer (printPdf),
 * skipping the backend PDF endpoints entirely. The layout mirrors the backend's
 * "logo across the whole top" mode (print_state >= 100 in DeliveryNoteExtentions.kt):
 * a full-width logo banner, company line, document title, client block, products table,
 * totals, comments and a signature box.
 *
 * The logo file is served from the app's public folder and is named after the DB
 * company, e.g. nexilcolab/public/shashalehamim.jpg (wide image, stretched to the full
 * page width preserving aspect ratio). If the file is missing the PDF is built without it.
 */
import { jsPDF } from 'jspdf';
import 'jspdf-autotable';
import JSDB from '../../../fromKotlin/nk';
import { printPdf, roundDecimals } from '../../../fromKotlin/nkutils';
import { hebrewPdfFont } from '../../../utils/csvManipulator';
import { ProductNote } from '../../../types/DocumentsTypes/Document';
import { productMainQty, productMainReturns, SummationLine } from './helpers';

const FONT_NAME = 'tahoma';
const HEB_RE = /[\u0590-\u05FF]/;
const MIRROR: Record<string, string> = { '(': ')', ')': '(', '[': ']', ']': '[', '{': '}', '}': '{', '<': '>', '>': '<' };

/**
 * Minimal bidi for jsPDF: token order is flipped and every token is reversed
 * char-by-char (Hebrew tokens also mirror brackets).
 *
 * Digit/latin tokens are pre-reversed too because jsPDF runs its own bidi pass
 * (postProcessText -> bidiEngine) on every string that contains RTL characters:
 * that pass keeps the Hebrew as given but reverses embedded LTR runs (numbers,
 * dates, latin words), so "101209" would come out "902101" unless we hand it
 * over already reversed. Strings without Hebrew are untouched by both passes.
 */
export const heb = (v: any): string => {
    const text = String(v ?? '');
    if (!HEB_RE.test(text)) return text;
    return text
        .split(/(\s+)/)
        .map((tok) =>
            HEB_RE.test(tok)
                ? tok
                      .split('')
                      .reverse()
                      .map((ch) => MIRROR[ch] ?? ch)
                      .join('')
                : tok
                      .split('')
                      .reverse()
                      .join('')
        )
        .reverse()
        .join('');
};

const fmt = (n: number): string => {
    const r = roundDecimals(n, 2);
    return Number.isInteger(r) ? String(r) : r.toFixed(2);
};

interface LogoData {
    dataUrl: string;
    ratio: number;
    /** jsPDF image format ('JPEG' / 'PNG'). */
    format: string;
}

let logoCache: LogoData | null | undefined;

/**
 * The banner file is named after the DB company, e.g. public/shashalehamim.jpg.
 * A company without such a file simply prints without the banner.
 */
const logoUrlCandidates = (): string[] => {
    const company = JSDB().getCompany?.() ?? '';
    if (!company) return [];
    return [`${company}.jpg`, `${company}.jpeg`, `${company}.png`].map((f) => `${process.env.PUBLIC_URL ?? ''}/${f}`);
};

/** Loads the company's top-banner logo once (null when no file is there). */
const loadLogo = async (): Promise<LogoData | null> => {
    if (logoCache !== undefined) return logoCache;
    logoCache = null;
    for (const url of logoUrlCandidates()) {
        try {
            // eslint-disable-next-line no-await-in-loop
            const resp = await fetch(url);
            // eslint-disable-next-line no-await-in-loop
            const blob = await resp.blob();
            // The dev server answers unknown paths with index.html, so check the mime type.
            if (!resp.ok || !blob.type.startsWith('image')) continue;
            // eslint-disable-next-line no-await-in-loop
            const dataUrl = await new Promise<string>((resolve, reject) => {
                const r = new FileReader();
                r.onload = () => resolve(r.result as string);
                r.onerror = reject;
                r.readAsDataURL(blob);
            });
            // eslint-disable-next-line no-await-in-loop
            const ratio = await new Promise<number>((resolve, reject) => {
                const img = new Image();
                img.onload = () => resolve(img.height / img.width);
                img.onerror = reject;
                img.src = dataUrl;
            });
            logoCache = { dataUrl, ratio, format: blob.type.includes('png') ? 'PNG' : 'JPEG' };
            break;
        } catch {
            // try the next candidate
        }
    }
    return logoCache;
};

interface DocContext {
    doc: jsPDF;
    pageWidth: number;
    pageHeight: number;
    margin: number;
    y: number;
    logo: LogoData | null;
}

const newDoc = async (): Promise<DocContext> => {
    const doc = new jsPDF('portrait', 'pt', 'A4');
    doc.addFileToVFS('tahoma-normal.ttf', hebrewPdfFont);
    doc.addFont('tahoma-normal.ttf', FONT_NAME, 'normal');
    doc.setFont(FONT_NAME);
    const logo = await loadLogo();
    return {
        doc,
        pageWidth: doc.internal.pageSize.getWidth(),
        pageHeight: doc.internal.pageSize.getHeight(),
        margin: 36,
        y: 0,
        logo
    };
};

/** Full-width logo banner at the very top of the current page (print_state>=100 style). */
const drawHeader = (ctx: DocContext) => {
    ctx.y = 24;
    if (ctx.logo) {
        // Stretch to the whole page width; cap the banner so it never eats the page.
        const h = Math.min(ctx.pageWidth * ctx.logo.ratio, 170);
        ctx.doc.addImage(ctx.logo.dataUrl, ctx.logo.format, 0, 0, ctx.pageWidth, h);
        ctx.y = h + 16;
    }
    const user = JSDB().getUser()?.first;
    const companyName = user?.user_company_print_name ?? '';
    const companyId = user?.user_buisness_number ?? '';
    if (companyName) {
        ctx.doc.setFontSize(16);
        ctx.doc.text(heb(companyName), ctx.pageWidth - ctx.margin, ctx.y, { align: 'right' });
        ctx.doc.setFontSize(11);
        if (companyId) ctx.doc.text(heb(`ח.פ / עוסק: ${companyId}`), ctx.margin, ctx.y, { align: 'left' });
        ctx.y += 20;
    }
};

const line = (ctx: DocContext) => {
    ctx.doc.setDrawColor(180);
    ctx.doc.line(ctx.margin, ctx.y, ctx.pageWidth - ctx.margin, ctx.y);
    ctx.y += 14;
};

const rightText = (ctx: DocContext, text: string, size = 11, gap = 16) => {
    ctx.doc.setFontSize(size);
    ctx.doc.text(heb(text), ctx.pageWidth - ctx.margin, ctx.y, { align: 'right' });
    ctx.y += gap;
};

/** Client / document details block, mirroring the backend title table. */
const drawEntBlock = (ctx: DocContext, note: ProductNote, title: string) => {
    const dateStr = note.date instanceof Date ? note.date.toLocaleDateString('he-IL') : String(note.date ?? '');
    ctx.doc.setFontSize(15);
    ctx.doc.text(heb(title), ctx.pageWidth / 2, ctx.y, { align: 'center' });
    ctx.y += 20;
    // Note date sits right below the document number.
    ctx.doc.setFontSize(12);
    ctx.doc.text(heb(`תאריך: ${dateStr}`), ctx.pageWidth / 2, ctx.y, { align: 'center' });
    ctx.y += 22;

    const client = JSDB().getClient(Number(note.entId))?.first;
    const issueStr = new Date().toLocaleString('he-IL', {
        day: '2-digit',
        month: '2-digit',
        year: 'numeric',
        hour: '2-digit',
        minute: '2-digit'
    });
    const bName = client?.getBusinessName?.() ?? note.entBName ?? '';
    const bId = client?.getBusinessId?.() ?? '';
    const address = client?.getAddressStr?.() ?? '';
    const phone = client?.getPhone?.() ?? '';

    rightText(ctx, `לכבוד: ${note.entName ?? ''}`, 13);
    if (bName && bName !== note.entName) rightText(ctx, `שם עסק: ${bName}`);
    if (bId) rightText(ctx, `ח.פ / עוסק: ${bId}`);
    if (address) rightText(ctx, `כתובת: ${address}`);
    if (phone) rightText(ctx, `טלפון: ${phone}`);
    ctx.doc.setFontSize(11);
    ctx.doc.text(heb(`הודפס: ${issueStr}`), ctx.margin, ctx.y - 16, { align: 'left' });
    if (note.agent) rightText(ctx, `סוכן: ${note.agent}`);
    if (note.orderId) rightText(ctx, `מס' הזמנה: ${note.orderId}`);
    ctx.y += 4;
    line(ctx);
};

interface TableLine {
    id: number;
    externalId?: string;
    name: string;
    qty: number;
    returns: number;
    unit: string;
    price: number;
    notes?: string;
}

const noteLines = (note: ProductNote): TableLine[] =>
    (note.products ?? [])
        .map((p: any) => ({
            id: p.id,
            externalId: p.external_id ?? undefined,
            name: p.name ?? `מוצר ${p.id}`,
            qty: productMainQty(p),
            returns: productMainReturns(p),
            unit: (p.available_units?.[0]) ?? p.unit ?? '',
            price: p.totalPrice ?? 0,
            notes: p.notes ?? ''
        }))
        .filter((l) => l.qty !== 0 || l.returns !== 0);

/** Which product-id column(s) to print, mirroring the touch display settings. */
interface IdColumnOptions {
    showId?: boolean;
    showExternalId?: boolean;
}

/** "id" / "extId" / "id [extId]" cell per the active settings (empty when both are off). */
const idCell = (l: TableLine, opts: IdColumnOptions): string => {
    const parts: string[] = [];
    if (opts.showId) parts.push(String(l.id));
    if (opts.showExternalId && l.externalId !== undefined && `${l.externalId}` !== '') parts.push(`[${l.externalId}]`);
    return parts.join(' ');
};

/**
 * Products table in visual RTL order (product name is the rightmost column, like the
 * backend note). Prices columns are added only when asked for and there are prices.
 */
const drawProductsTable = (ctx: DocContext, lines: TableLine[], withPrices: boolean, idOpts: IdColumnOptions) => {
    const anyReturns = lines.some((l) => l.returns > 0);
    const anyNotes = lines.some((l) => (l.notes ?? '').trim() !== '');
    const showPrices = withPrices && lines.some((l) => l.price > 0);
    const showIdCol = Boolean(idOpts.showId) || (Boolean(idOpts.showExternalId) && lines.some((l) => `${l.externalId ?? ''}` !== ''));

    // Visual order, right-to-left: [notes?] [sum?] [price?] [total?] [returns?] [qty] [unit] [name] [id?]
    const head: string[] = [];
    const rowOf = (l: TableLine): string[] => {
        const cells: string[] = [];
        const net = l.qty - l.returns;
        if (anyNotes) cells.push(heb(l.notes ?? ''));
        if (showPrices) {
            cells.push(fmt(net * l.price));
            cells.push(fmt(l.price));
        }
        if (anyReturns) {
            cells.push(fmt(net));
            cells.push(l.returns ? fmt(l.returns) : '');
        }
        cells.push(fmt(l.qty));
        cells.push(heb(l.unit));
        cells.push(heb(l.name));
        if (showIdCol) cells.push(heb(idCell(l, idOpts)));
        return cells;
    };
    if (anyNotes) head.push(heb('הערות'));
    if (showPrices) {
        head.push(heb('סכום ₪'));
        head.push(heb('מחיר ₪'));
    }
    if (anyReturns) {
        head.push(heb("סה''כ"));
        head.push(heb('חזרות'));
    }
    head.push(heb('כמות'));
    head.push(heb('יחידה'));
    head.push(heb('שם מוצר'));
    if (showIdCol) head.push(heb('מזהה'));

    const totalQty = lines.reduce((s, l) => s + l.qty, 0);
    const totalReturns = lines.reduce((s, l) => s + l.returns, 0);
    const totalSum = lines.reduce((s, l) => s + (l.qty - l.returns) * l.price, 0);
    const footer: string[] = [];
    if (anyNotes) footer.push('');
    if (showPrices) {
        footer.push(fmt(totalSum));
        footer.push('');
    }
    if (anyReturns) {
        footer.push(fmt(totalQty - totalReturns));
        footer.push(fmt(totalReturns));
    }
    footer.push(fmt(totalQty));
    footer.push('');
    footer.push(heb("סה''כ"));
    if (showIdCol) footer.push('');

    (ctx.doc as any).autoTable({
        startY: ctx.y,
        head: [head],
        body: lines.map(rowOf),
        foot: [footer],
        margin: { left: ctx.margin, right: ctx.margin, top: ctx.logo ? Math.min(ctx.pageWidth * ctx.logo.ratio, 170) + 20 : 40 },
        styles: { font: FONT_NAME, fontSize: 11, halign: 'right', cellPadding: 5 },
        // The embedded Hebrew font only ships a normal weight - bold would fall back
        // to a latin font and garble the Hebrew headers.
        headStyles: { fillColor: [134, 198, 234], textColor: 20, halign: 'right', fontStyle: 'normal' },
        footStyles: { fillColor: [230, 230, 230], textColor: 20, halign: 'right', fontStyle: 'normal' },
        columnStyles: {
            // The product-name column stays flexible; the id column stays compact.
            [showIdCol ? head.length - 2 : head.length - 1]: { cellWidth: 'auto' },
            ...(showIdCol ? { [head.length - 1]: { cellWidth: 70 } } : {})
        },
        didDrawPage: () => {
            // Re-draw the logo banner on overflow pages.
            if (ctx.logo) {
                const h = Math.min(ctx.pageWidth * ctx.logo.ratio, 170);
                ctx.doc.addImage(ctx.logo.dataUrl, ctx.logo.format, 0, 0, ctx.pageWidth, h);
            }
        }
    });
    ctx.y = (ctx.doc as any).lastAutoTable.finalY + 20;
};

const drawFooter = (ctx: DocContext, note: ProductNote) => {
    if ((note.comment ?? '').trim() !== '') {
        rightText(ctx, `הערות: ${note.comment}`, 12, 18);
    }
    // Signature box (kept on the current page when there's room).
    if (ctx.y + 60 < ctx.pageHeight - 30) {
        ctx.y += 14;
        ctx.doc.setFontSize(11);
        ctx.doc.text(heb('חתימת הלקוח'), ctx.pageWidth - ctx.margin - 60, ctx.y, { align: 'center' });
        ctx.doc.setDrawColor(60);
        ctx.doc.roundedRect(ctx.pageWidth - ctx.margin - 130, ctx.y + 8, 140, 40, 4, 4);
        ctx.doc.setFontSize(13);
        ctx.doc.text('X', ctx.pageWidth - ctx.margin - 120, ctx.y + 34);
    }
};

const DOC_TITLES: Record<string, string> = {
    delivery_certificate: 'תעודת משלוח',
    order: 'הזמנה'
};

/**
 * Whether the printed note should carry price columns, following the backend rule
 * (DeliveryNoteExtentions.buildSingleDV): the client's print_state must have the
 * price bit (4) and not be in value-summary mode (bit 8).
 */
const clientWantsPrices = (note: ProductNote): boolean => {
    const ps = Number(JSDB().getClient(Number(note.entId))?.first?.print_state ?? 0);
    // eslint-disable-next-line no-bitwise
    return Boolean(ps & 4) && !(ps & 8);
};

/**
 * Builds a delivery-note / order PDF locally and sends it straight to the printer.
 * `notes` are the already-deserialized documents (each gets its own page sequence).
 * `idOpts` mirrors the touch display settings (show product id / external id column).
 */
export const printNoteLocally = async (notes: ProductNote[], idOpts: IdColumnOptions = {}): Promise<void> => {
    const ctx = await newDoc();
    notes.forEach((note, i) => {
        if (i > 0) {
            ctx.doc.addPage();
        }
        drawHeader(ctx);
        const baseTitle = DOC_TITLES[note.type] ?? 'תעודת משלוח';
        const canceled = note.status === 'canceled' ? ' (מבוטל)' : '';
        const returns = (note as any).returnsOnly ? ' - החזרות' : '';
        drawEntBlock(ctx, note, `${baseTitle}${returns} מס' ${note.id}${canceled}`);
        drawProductsTable(ctx, noteLines(note), clientWantsPrices(note), idOpts);
        drawFooter(ctx, note);
    });
    printPdf(ctx.doc.output('arraybuffer'));
};

/** Builds the daily production summary PDF locally (from the on-screen summation). */
export const printSummaryLocally = async (
    lines: SummationLine[],
    subtitle: string
): Promise<void> => {
    const ctx = await newDoc();
    drawHeader(ctx);
    ctx.doc.setFontSize(15);
    ctx.doc.text(heb(`סיכום ייצור - ${subtitle}`), ctx.pageWidth / 2, ctx.y, { align: 'center' });
    ctx.y += 20;
    (ctx.doc as any).autoTable({
        startY: ctx.y,
        head: [[heb('הזמנות'), heb('כמות'), heb('יחידה'), heb('שם מוצר')]],
        body: lines.map((l) => [String(l.orders), fmt(l.total), heb(l.unit), heb(l.name)]),
        margin: { left: ctx.margin, right: ctx.margin, top: ctx.logo ? Math.min(ctx.pageWidth * ctx.logo.ratio, 170) + 20 : 40 },
        styles: { font: FONT_NAME, fontSize: 12, halign: 'right', cellPadding: 6 },
        headStyles: { fillColor: [134, 198, 234], textColor: 20, halign: 'right', fontStyle: 'normal' },
        didDrawPage: () => {
            if (ctx.logo) {
                const h = Math.min(ctx.pageWidth * ctx.logo.ratio, 170);
                ctx.doc.addImage(ctx.logo.dataUrl, ctx.logo.format, 0, 0, ctx.pageWidth, h);
            }
        }
    });
    printPdf(ctx.doc.output('arraybuffer'));
};
